import { useMemo, useState } from "react";
import { MessageCircle, Calculator } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { WA, PRICES, fmtCOP } from "@/content";

export default function Roi() {
  const [tests, setTests] = useState(20);
  const [price, setPrice] = useState(12000);
  const [days, setDays] = useState(26);

  const { monthly, annual, payback } = useMemo(() => {
    const monthly = tests * price * days;
    const payback = monthly > 0 ? 15900000 / monthly : 0;
    return { monthly, annual: monthly * 12, payback };
  }, [tests, price, days]);

  const slider = (label, value, min, max, step, set, testid, display) => (
    <div data-testid={`${testid}-wrapper`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold text-slate-600">{label}</span>
        <span data-testid={`${testid}-value`} className="font-mono2 text-sm font-bold text-brand-orange">
          {display}
        </span>
      </div>
      <input
        type="range"
        data-testid={testid}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="w-full accent-brand-orange h-2 cursor-pointer"
      />
    </div>
  );

  return (
    <section data-testid="roi-section" className="relative py-20 lg:py-28 bg-brand-cloud">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-[0_40px_90px_-50px_rgba(15,23,42,0.35)]">
            <div
              aria-hidden="true"
              className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(255,87,34,0.14),transparent_65%)]"
            />
            <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-14 p-8 sm:p-12 lg:p-14">
              <div>
                <SectionHead
                  eyebrow="Calculadora de retorno"
                  title="¿Cuánto vale llevar el laboratorio a tu consultorio?"
                  sub="Mueve los controles con tus propias cifras y estima en segundos el potencial mensual del hemograma dentro de tu clínica."
                />
                <div className="mt-9 space-y-7" data-testid="roi-calculator-controls">
                  {slider(
                    "Pruebas por día",
                    tests,
                    5,
                    100,
                    1,
                    setTests,
                    "roi-tests-slider",
                    `${tests} pruebas`
                  )}
                  {slider(
                    "Precio por prueba",
                    price,
                    5000,
                    30000,
                    500,
                    setPrice,
                    "roi-price-slider",
                    fmtCOP(price)
                  )}
                  {slider(
                    "Días de trabajo al mes",
                    days,
                    16,
                    30,
                    1,
                    setDays,
                    "roi-days-slider",
                    `${days} días`
                  )}
                </div>
                <p className="mt-7 font-mono2 text-[11px] leading-relaxed text-slate-400 uppercase tracking-wider" data-testid="roi-disclaimer">
                  Cálculo referencial con valores editables · No descuenta el costo de reactivos
                </p>
              </div>

              <div className="flex flex-col justify-center gap-5">
                <div className="grid sm:grid-cols-2 gap-5" data-testid="roi-results">
                  <div className="rounded-3xl bg-brand-cloud border border-slate-200 p-6">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Ingreso mensual
                    </span>
                    <p data-testid="roi-monthly-result" className="font-display text-2xl sm:text-3xl font-extrabold text-brand-ink mt-2">
                      {fmtCOP(monthly)}
                    </p>
                  </div>
                  <div className="rounded-3xl bg-brand-cloud border border-slate-200 p-6">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Ingreso anual
                    </span>
                    <p data-testid="roi-annual-result" className="font-display text-2xl sm:text-3xl font-extrabold text-brand-ink mt-2">
                      {fmtCOP(annual)}
                    </p>
                  </div>
                </div>

                <div className="rounded-3xl bg-brand-ink text-white p-7 sm:p-8 grain">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-brand-orange">
                    Recupero estimado del equipo
                  </span>
                  <p data-testid="roi-payback-result" className="font-display text-4xl sm:text-5xl font-extrabold mt-2">
                    {payback < 1 ? "< 1" : payback.toFixed(1)} meses
                  </p>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    Con el precio de lanzamiento de {PRICES.now} {PRICES.iva}, el equipo puede pagarse solo con tu
                    facturación de hemogramas.
                  </p>
                  <a
                    href={WA.quote}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="roi-whatsapp-cta"
                    className="btn-shine mt-6 inline-flex items-center gap-2 rounded-full bg-brand-orange text-white font-bold text-sm px-6 py-3.5 hover:bg-brand-deep transition-colors duration-300"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Quiero cotizar mi caso
                  </a>
                </div>

                <p className="flex items-center gap-2 text-xs text-slate-400 font-semibold" data-testid="roi-note">
                  <Calculator className="h-4 w-4" />
                  Ajusta los valores a la realidad de tu zona.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
